import { useEffect, useState } from "react";
import { getDataForEmployee } from "../api/Employees";
import { EmployeePhone } from "../models/employees/Employees";
import {
  extractPhonesFromEmployeeData,
  getEmailFromEmployeeData,
} from "../util/EmployeeContact";

export interface EmployeeContact {
  email?: string;
  phones: EmployeePhone[];
}

// Module-level cache so the same employee referenced by multiple WO fields
// (e.g. Requestor and Assigned To both = "sakti") is only fetched once.
const cache = new Map<string, EmployeeContact>();
const inflight = new Map<string, Promise<EmployeeContact>>();

const fetchContact = (employeeId: string): Promise<EmployeeContact> => {
  const cached = cache.get(employeeId);
  if (cached) return Promise.resolve(cached);
  const existing = inflight.get(employeeId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const resp = await getDataForEmployee(employeeId);
      const contact: EmployeeContact = {
        email: getEmailFromEmployeeData(resp?.data),
        phones: extractPhonesFromEmployeeData(resp?.data),
      };
      cache.set(employeeId, contact);
      return contact;
    } catch {
      const empty: EmployeeContact = { phones: [] };
      cache.set(employeeId, empty);
      return empty;
    } finally {
      inflight.delete(employeeId);
    }
  })();
  inflight.set(employeeId, promise);
  return promise;
};

/**
 * Resolves an employee's contact info (email + phones) by id, cached across
 * the app. Returns undefined contact until loaded.
 */
export default function useEmployeeContact(employeeId?: string) {
  const [contact, setContact] = useState<EmployeeContact | undefined>(
    employeeId ? cache.get(employeeId) : undefined
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!employeeId) {
      setContact(undefined);
      return;
    }
    const cached = cache.get(employeeId);
    if (cached) {
      setContact(cached);
      return;
    }
    setLoading(true);
    fetchContact(employeeId).then((c) => {
      if (active) {
        setContact(c);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [employeeId]);

  return { contact, loading };
}
